import React, { useState } from 'react';
import type { ConnectedSourceId, Outlet } from '../types';
import { Icon } from './shared/Icon';
import { EventMapPopup } from './EventMapPopup';

type OutletEvent = NonNullable<Outlet['events']>[number];

function fmtHour(h: number): string {
  return `${h}:00`;
}

function fmtDist(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(1)}km` : `${m}m`;
}

export function WeatherTile({
  outlets,
  connected,
  onConnect,
}: {
  outlets: Outlet[];
  connected: ConnectedSourceId[];
  onConnect?: (id: ConnectedSourceId) => void;
}) {
  const weatherOn = connected.includes('weather');
  // The dinner-service outlook (later today) is what moves guests; fall back to
  // current conditions when there's no evening window left.
  const svc = (o: Outlet) => o.weather?.later ?? o.weather;
  // One representative reading per city (weather is shared within a city), but
  // only surface cities where the weather can actually move occupancy - a
  // meaningful demand lift/suppression, or a meaningful change later today.
  const matters = (o: Outlet) => {
    const s = svc(o);
    return !!s && (Math.abs(s.mult - 1) >= 0.05 || !!o.weather!.later?.changing);
  };
  const byCity = new Map<string, Outlet>();
  if (weatherOn) {
    for (const o of outlets) {
      if (matters(o) && !byCity.has(o.city)) {
        byCity.set(o.city, o);
      }
    }
  }
  const rows = Array.from(byCity.values()).sort(
    (a, b) => Math.abs(svc(b)!.mult - 1) - Math.abs(svc(a)!.mult - 1),
  );

  return (
    <div className="ol-card">
      <div className="ol-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="cloud" size={16} style={{ color: 'var(--accent)' }} />
          <h3>Weather</h3>
        </div>
      </div>
      {!weatherOn ? (
        <div className="sig-empty">
          <p>
            Connect weather to layer demand impact onto the forecast - rain
            lifts indoor dwell, clear skies open terrace guests.
          </p>
          {onConnect ? (
            <button
              type="button"
              className="kpi-lock-cta"
              onClick={() => onConnect('weather')}
            >
              <Icon name="database" size={13} /> Connect Weather
            </button>
          ) : (
            <p>Connect weather to see demand impact.</p>
          )}
        </div>
      ) : rows.length === 0 ? (
        <p className="sig-empty">
          Calm weather across scoped outlets - no occupancy impact expected.
        </p>
      ) : (
        <div className="sig-list">
          <div className="sig-group">
            <span className="sig-group-label">Now → tonight · by city</span>
            {rows.map(o => {
              const s = svc(o)!;
              const later = o.weather!.later;
              const changing = !!later?.changing;
              return (
                <div key={o.city} className="sig-row">
                  <Icon
                    name={s.icon}
                    size={15}
                    style={{
                      color:
                        s.mult >= 1.02
                          ? 'var(--st-steady)'
                          : s.mult < 0.95
                            ? 'var(--st-alert)'
                            : 'var(--ink-3)',
                    }}
                  />
                  <span className="sig-row-name">{o.city}</span>
                  <span className="sig-row-meta">
                    {o.weather!.temp}° · {o.weather!.cond}
                    {changing && (
                      <span className="sig-row-trend">
                        {' → '}
                        {later!.cond} by {later!.hour}:00
                      </span>
                    )}
                  </span>
                  <span className="sig-row-note">{s.impact}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function EventsTile({
  outlets,
  connected,
  onConnect,
}: {
  outlets: Outlet[];
  connected: ConnectedSourceId[];
  onConnect: (id: ConnectedSourceId) => void;
}) {
  const eventsOn = connected.includes('events');
  const [selected, setSelected] = useState<{
    ev: OutletEvent;
    outlet: string;
    lat?: number;
    lng?: number;
    address?: string;
  } | null>(null);
  const eventRows = eventsOn
    ? outlets
        .filter(o => o.events && o.events.length)
        .flatMap(o =>
          o.events!.map(ev => ({
            outlet: o.name,
            lat: o.lat,
            lng: o.lng,
            address: o.address,
            ev,
          })),
        )
        .sort(
          (a, b) =>
            Number(b.ev.surge) - Number(a.ev.surge) || a.ev.dist - b.ev.dist,
        )
    : [];

  return (
    <div className="ol-card">
      {selected && (
        <EventMapPopup
          event={selected.ev}
          outletName={selected.outlet}
          restLat={selected.lat}
          restLng={selected.lng}
          restAddress={selected.address}
          onClose={() => setSelected(null)}
        />
      )}
      <div className="ol-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="ticket" size={16} style={{ color: 'var(--accent)' }} />
          <h3>Nearby events</h3>
        </div>
      </div>
      {!eventsOn ? (
        <div className="sig-empty">
          <p>
            Connect local events to catch demand waves - concerts, matches and
            premieres within walking distance create post-show walk-ins.
          </p>
          <button
            type="button"
            className="kpi-lock-cta"
            onClick={() => onConnect('events')}
          >
            <Icon name="database" size={13} /> Connect Events
          </button>
        </div>
      ) : eventRows.length === 0 ? (
        <p className="sig-empty">No events near scoped outlets tonight.</p>
      ) : (
        <div className="sig-list">
          <div className="sig-group">
            <span className="sig-group-label">Tonight · walking distance</span>
            {eventRows.map((row, i) => (
              <div
                key={`${row.outlet}-${i}`}
                className="sig-row sig-row-clickable"
                role="button"
                tabIndex={0}
                title="View on map"
                onClick={() =>
                  setSelected({
                    ev: row.ev,
                    outlet: row.outlet,
                    lat: row.lat,
                    lng: row.lng,
                    address: row.address,
                  })
                }
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelected({
                      ev: row.ev,
                      outlet: row.outlet,
                      lat: row.lat,
                      lng: row.lng,
                      address: row.address,
                    });
                  }
                }}
              >
                <Icon
                  name="ticket"
                  size={15}
                  style={{
                    color: row.ev.surge ? 'var(--accent)' : 'var(--ink-3)',
                  }}
                />
                <span className="sig-row-name">{row.ev.name}</span>
                <span className="sig-row-meta">
                  {fmtHour(row.ev.start)} · {fmtDist(row.ev.dist)}
                </span>
                <span className="sig-row-note">
                  near {row.outlet}
                  {row.ev.surge ? ' · surge expected' : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ExternalSignals({
  outlets,
  connected,
  onConnect,
}: {
  outlets: Outlet[];
  connected: ConnectedSourceId[];
  onConnect: (id: ConnectedSourceId) => void;
}) {
  const open = outlets.filter(o => !o.closed);
  return (
    <section className="outlook" data-ftue="live-signals">
      <div className="sec-head">
        <h2>External signals</h2>
        <span className="sh-meta">
          Weather &amp; nearby events shaping tonight&apos;s demand
        </span>
      </div>
      <div className="signals-grid">
        <WeatherTile
          outlets={open}
          connected={connected}
          onConnect={onConnect}
        />
        <EventsTile outlets={open} connected={connected} onConnect={onConnect} />
      </div>
    </section>
  );
}
